import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { CheckRounded, ExpandMoreOutlined } from "@mui/icons-material";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import HeadlessButton from "./Button";

export default function HeadlessComboboxInLine({
  value,
  name,
  options,
  pageSize = 5,
  errorMsg,
  className,
  placeholder,
  onChange,
  disabled,
  ...restProps
}) {
  const [selectedOption, setSelectedOption] = useState(value || null);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    setSelectedOption(value || null);
  }, [value]);

  useEffect(() => {
    setPage(1);
  }, [pageSize, query]);

  const filteredOptions = useMemo(() => {
    if (query === "") {
      return options;
    } else {
      return options.filter((option) => {
        return option.value.toLowerCase().includes(query.toLowerCase());
      });
    }
  }, [options, query]);

  const totalPages = useMemo(
    () => Math.ceil(filteredOptions.length / pageSize),
    [filteredOptions.length, pageSize]
  );

  const optionsForPage = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredOptions.slice(startIndex, endIndex);
  }, [filteredOptions, page, pageSize]);

  const handleChange = (value) => {
    setSelectedOption(value);
    setQuery("");
    onChange(value);
  };
  return (
    <Combobox
      value={selectedOption}
      onChange={(value) => {
        handleChange(value);
      }}
      disabled={disabled}
      onClose={() => setQuery("")}
    >
      <div className={`tw-relative ${className}`}>
        <ComboboxInput
          className={clsx(
            errorMsg &&
              "tw-ring-1 tw-border-none tw-outline-none tw-ring-error-light",
            "tw-text-xs tw-w-full tw-rounded-md tw-border tw-border-boarder-light tw-py-1 tw-pr-8 tw-pl-2 tw-h-7 tw-text-text-primary-light",
            "tw-focus:outline-none tw-focus:border-none data-[focus]:tw-ring-1",
            errorMsg
              ? "data-[focus]:tw-ring-error-light"
              : "data-[focus]:tw-ring-focus"
          )}
          displayValue={(option) => option?.value}
          onChange={(event) => {
            setQuery(event.target.value);
            handleChange(null);
          }}
          name={name}
          id={name}
          {...restProps}
        />

        <ComboboxButton
          className="tw-group tw-absolute tw-inset-y-0 tw-right-0 tw-px-2.5"
          data-testid="combo-button"
        >
          <ExpandMoreOutlined className="tw-size-1 tw-fill-text-primary-light group-data-[hover]:tw-fill-boarder-light" />
        </ComboboxButton>

        {optionsForPage.length > 0 && (
          <ComboboxOptions
            transition
            className={clsx(
              "tw-absolute tw-mt-1 tw-z-10 tw-w-full tw-rounded-md tw-border tw-border-boarder-light tw-bg-white tw-p-1 [--anchor-gap:4px] ",
              "tw-transition tw-duration-100 tw-ease-in data-[leave]:data-[closed]:tw-opacity-0"
            )}
          >
            {optionsForPage.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                className="tw-group tw-flex tw-cursor-pointer tw-items-left tw-gap-1 tw-rounded-lg tw-select-none data-[focus]:tw-bg-disabled-bg-light"
              >
                <CheckRounded className="tw-invisible tw-size-1 tw-fill-white group-data-[selected]:tw-visible" />
                <div className="tw-text-xs tw-text-text-primary-light">
                  {option.value}
                </div>
              </ComboboxOption>
            ))}
            <div className="tw-flex tw-justify-between tw-items-center tw-py-1 tw-px-1">
              <HeadlessButton
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                theme="none"
              >
                {t("Combobox.previous")}
              </HeadlessButton>

              <span className="tw-text-text-primary-light">
                {t("Combobox.pages", { page: page, total: totalPages })}
              </span>

              <HeadlessButton
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                theme="none"
              >
                {t("Combobox.next")}
              </HeadlessButton>
            </div>
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}
